import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/shared/supabase/client';

const TEST_EMAILS = ['test@sinvco.com', 'admin@sinvco.com'];

interface TestModeContextType {
  isTestUser: boolean;
  isTestMode: boolean;
  isTestLoading: boolean;
  toggleTestMode: () => void;
}

const TestModeContext = createContext<TestModeContextType>({
  isTestUser: false,
  isTestMode: false,
  isTestLoading: true,
  toggleTestMode: () => {},
});

export function TestModeProvider({ children }: { children: ReactNode }) {
  const [isTestUser, setIsTestUser] = useState(false);
  const [isTestLoading, setIsTestLoading] = useState(true);
  const [isTestMode, setIsTestMode] = useState(() => {
    return sessionStorage.getItem('medtour_test_mode') === 'true' || sessionStorage.getItem('kh_test_mode') === 'true';
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const testUser = TEST_EMAILS.includes(session?.user?.email || '');
      setIsTestUser(testUser);
      if (!testUser) {
        setIsTestMode(false);
        sessionStorage.removeItem('medtour_test_mode');
        sessionStorage.removeItem('kh_test_mode');
      }
      setIsTestLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      const testUser = TEST_EMAILS.includes(session?.user?.email || '');
      setIsTestUser(testUser);
      if (!testUser) {
        setIsTestMode(false);
        sessionStorage.removeItem('medtour_test_mode');
        sessionStorage.removeItem('kh_test_mode');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleTestMode = useCallback(() => {
    if (!isTestUser) return;
    setIsTestMode(prev => {
      const next = !prev;
      if (next) {
        sessionStorage.setItem('medtour_test_mode', 'true');
        sessionStorage.removeItem('kh_test_mode');
      } else {
        sessionStorage.removeItem('medtour_test_mode');
        sessionStorage.removeItem('kh_test_mode');
      }
      return next;
    });
  }, [isTestUser]);

  return (
    <TestModeContext.Provider value={{ isTestUser, isTestMode, isTestLoading, toggleTestMode }}>
      {children}
    </TestModeContext.Provider>
  );
}

export function useTestMode() {
  return useContext(TestModeContext);
}
